<template>
    <div v-if="hasTabs" ref="tabsContainer" class="parking-tabs">
        <div class="parking-tabs__container md:container" :class="{ 'is-tabs-open': isOpen }">
            <aside class="parking-tabs__controls">
                <ul class="tabs-controls">
                    <li v-for="label in labels" :key="label.href" class="tabs-controls__item">
                        <a
                            :href="`#${label.href}`"
                            class="tabs-controls__btn"
                            :class="{ 'is-active': activeTab === label.tab }"
                            @click="setActiveTab(label.tab)"
                        >
                            {{ label.label }}
                        </a>
                    </li>
                </ul>
                <select id="tabs-select" v-model="activeTab" name="tabs-select" class="tabs-select">
                    <option v-for="label in labels" :key="label.href" :value="label.tab">
                        {{ label.label }}
                    </option>
                </select>
            </aside>
            <div ref="tabsContentWrapperRef" class="parking-tabs__content" :class="{ 'is-open': isOpen }">
                <template v-for="{ name, content, id } in airportContent" :key="name">
                    <article
                        v-if="activeTab === name"
                        :id="id"
                        :ref="(el: HTMLElement) => setItemRef(el, name)"
                        class="tabs-content"
                    >
                        <SocialShare v-if="hasShare" :share="share" />
                        <template v-for="(_el, index) in content" :key="index">
                            <component
                                :is="vueComponents[_el.vueComponent]"
                                v-if="_el.vueComponent"
                                v-bind="_el.componentProps"
                            />
                            <rich-text :content="_el" />
                        </template>
                    </article>
                </template>
                <div class="parking-tabs__content-toggler">
                    <button
                        v-if="isShowMoreBtnVisible"
                        class="parking-tabs__content-toggler-button"
                        @click="isOpen = !isOpen"
                    >
                        {{ isOpen ? t('tabs.show_less') : t('tabs.show_more') }}
                    </button>

                    <a href="#">
                        <ComparePrices type-compare="button" class-compare="submit right-icon comparePrices-sm" />
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { IL4AirportParkingTabsConfig } from '~/types/components/L4AirportParkingTabs';
import { convertToKebabCase } from '~/utils/helpers';
import { fixContentItemLinks } from '~/utils/fixRichTextLinks';
import l4_tabsKeyPoints from '~/components/Tabs/KeyPoints.vue';
import l4_tabsMap from '~/components/Tabs/Map.vue';
import l4_tabsTable from '~/components/Tabs/Table.vue';

const { t } = useI18n();

// Is needed for correct tabs order
const orders: string[] = [
    'parking',
    'transfers',
    'overview',
    'facilities',
    'hotel_and_parking',
    'things_to_do_in',
    'things_to_do_near',
    'terminal',
    'how_to_get',
    'useful_tips',
    'faq',
];

function hashClass(string: string): string {
    return convertToKebabCase(string.replace("'", ''));
}

interface ITabsContent {
    name: string;
    id: string;
    content: any;
}

// Set data
const { blok } = defineProps<{ blok: IL4AirportParkingTabsConfig }>();
const { airport, share, tabs } = blok;
const l4AirportContent = blok.airport_content[0] ?? [];
const vueComponents = {
    l4_tabsKeyPoints,
    l4_tabsMap,
    l4_tabsTable,
};

const tabsContainer = ref<HTMLDivElement>();
const activeTab = ref<string>('');
const isOpen = ref<boolean>(false);
const activatedScrollBehaviour = ref<boolean>(false);
const isShowMoreBtnVisible = ref<boolean>(true);
const tabsContentWrapperRef = ref<HTMLDivElement>();
const tabsContentRefs: any = {};

watch(activeTab, () => {
    isOpen.value = false;

    setTimeout(() => {
        if (activatedScrollBehaviour.value) {
            window.scrollTo(0, (tabsContainer.value as HTMLDivElement).offsetTop - 150);
        }
        const activeEl = tabsContentRefs[activeTab.value];
        const contentHeight = activeEl?.offsetHeight || 0;
        const wrapperHeight = tabsContentWrapperRef?.value?.offsetHeight || 0;
        isShowMoreBtnVisible.value = contentHeight - wrapperHeight > -90;
    }, 10);
});

// Check for non-empty
const hasTabs: ComputedRef<boolean> = computed(() => !!airportContent.value.length);
const hasShare: ComputedRef<boolean> = computed(() => share && share.length > 0);

onMounted(() => {
    if (typeof window !== 'undefined') {
        const hash = window.location.hash.replace('#', '');
        const activeTab = airportContent.value.filter((content) => hashClass(content.id).includes(hash));
        const hasMatchingHash = hash && activeTab.length > 0;

        if (window.location.hash && hasMatchingHash) {
            activatedScrollBehaviour.value = true;
            setActiveTab(activeTab[0].name);
        } else {
            // Prevent auto-scroll to tabs, when there is no hash
            setActiveTab(tabs[0]);
            setTimeout(() => {
                activatedScrollBehaviour.value = true;
            }, 50);
        }
    }
});

interface ITabLabel {
    href: string;
    label: string;
    tab: string;
}

// Computed
const labels: ComputedRef<ITabLabel[]> = computed(() =>
    tabs
        .sort((a: string, b: string) => orders.indexOf(a) - orders.indexOf(b))
        .filter((tab) => l4AirportContent[tab]?.content?.length)
        .map((tab) => {
            const label = blok[`${tab}_label`].replace(
                `{airport}`,
                (airport?.content ? airport.content.code : '').toUpperCase() || ''
            );
            const href = hashClass(label);
            return {
                href,
                label,
                tab,
            };
        })
);

const { render } = useStoryblokRichText({});
const richText = (content: any) => {
    if (content) {
        return render(content.content);
    }
};

const airportContent: ComputedRef<ITabsContent[]> = computed(() =>
    tabs.reduce((content: ITabsContent[], tab) => {
        if (tab in l4AirportContent && l4AirportContent[tab]?.content?.length) {
            const parcedContent = l4AirportContent[tab].content?.map((_el: any) => {
                const el: any = {};
                if (_el.type === 'blok') {
                    const body = _el.attrs.body[0];
                    el.vueComponent = body.component;
                    el.componentProps = {};
                    el.componentProps.airport = airport;
                    el.componentProps.airportContent = l4AirportContent;
                    el.componentProps.enabled = body.enabled;
                    el.componentProps.type = body.type;
                } else {
                    // Adding type: 'doc' it's needed for correct rendering of RichText
                    const c: any = {};
                    c.content = [_el];
                    fixContentItemLinks(c.content);
                    c.type = 'doc';
                    return c;
                }
                return el;
            });

            const label: any = labels.value.find((l) => l.tab === tab);

            content.push({
                name: tab,
                id: label.href,
                content: parcedContent,
            });
        }
        return content;
    }, [])
);

function setActiveTab(tab: string): void {
    activeTab.value = tab;
}

function setItemRef(el: HTMLElement, name: string) {
    if (el) {
        tabsContentRefs[name] = el;
    }
}
</script>

<style lang="postcss" scoped>
.parking-tabs {
    @apply my-5 bg-white md:bg-transparent mx-2;
    &__container {
        @apply flex flex-col md:flex-row gap-5;
        &.is-tabs-open {
            @apply md:items-start;
        }
    }
    &__controls {
        @apply sticky top-[64px] z-[99] bg-white p-2.5 md:p-0 md:w-1/4 shrink-0;
    }
    &__content {
        @apply flex-grow bg-white overflow-hidden relative md:min-h-[500px];
        &.is-open {
            @apply overflow-visible;
        }
        &-toggler {
            @apply relative md:absolute bottom-0 left-0 w-full flex pt-0 md:pt-4 p-4 flex-col;
            .is-open & {
                @apply relative;
            }
            &:before {
                content: '';
                @apply hidden md:block absolute bottom-0 left-0 w-full h-[180px] z-0;
                background: linear-gradient(180deg, hsla(0, 0%, 100%, 0) 0, white 50%);
                .is-open & {
                    @apply hidden;
                }
            }
            &-button {
                @apply hidden md:block relative z-10 text-primary underline font-bold mr-auto mb-2;
            }
        }
    }
}

.tabs-controls {
    @apply bg-white hidden md:block;
    &__item {
        &:not(:last-child) {
            border-bottom: 1px solid #ddd;
        }
    }
    &__btn {
        @apply p-4 bg-white text-bodypurple w-full text-left block;
        &.is-active {
            @apply text-white;
            background: linear-gradient(#4c2262, #6d2f84);
        }
    }
}

.tabs-select {
    @apply px-2.5 py-1.5 bg-white w-full text-lg relative m-0 md:hidden h-auto;
    border: 1px solid black;
}

.tabs-content {
    @apply md:absolute p-4 text-primary;
    .is-open & {
        @apply relative;
    }
    &:deep(h2, h3) {
        @apply font-semibold;
    }
    &:deep(h2) {
        @apply font-semibold text-xl;
    }
    &:deep(h3) {
        @apply font-semibold text-lg leading-none mt-3;
    }
    &:deep(h2 + h3) {
        margin-top: 15px !important;
    }
    &:deep(p) {
        @apply my-3;
    }
    &:deep(ul) {
        @apply list-disc pl-10;
    }
    &:deep(ul p) {
        @apply my-0;
    }
}
</style>
